import { MAX_SPLIT_MEMBERS } from "../../shared/split_limits";
/**
 * Row types, and the validation boundary between the wire and Dexie (W-5).
 *
 * Everything arriving from the network is `unknown` until it has been through a
 * `fromWire*` function here. A shape mismatch throws (INV-7) — it is never
 * coerced, defaulted, or partially accepted. A malformed row that reaches Dexie
 * is a row the UI will render as fact.
 *
 * **These validate camelCase**, because the payload IS Aegae's own domain row,
 * serialised verbatim. There is no snake_case boundary: the server stores the
 * JSON opaquely and never reads inside it.
 *
 * > This file was previously the Supabase mapper and validated snake_case. That
 * > mismatch meant every pushed row failed validation on pull — push worked,
 * > pull threw on the first row, and sync died permanently. It survived because
 * > the tests stubbed pull as `{ rows: [] }`. `sync.test.ts` now round-trips a
 * > real row through push → pull → validate to make that impossible.
 */

import { assertSplitOccurredAt, SplitRowError } from "@/domain/splits";
import { EXPENDITURE_LABEL } from "@/domain/category_kind";
import { type Cents, type Minor, type RateNano, cents, minor, rateNano, MAX_RATE_NANO } from "@/domain/money";

import { isCurrencyCode, MAX_MINOR_INPUT } from "../../shared/currencies";
import { validateSplit, validateTransfer, type SplitMode, type SplitEntry } from "../../shared/split_rules";

export type RolloverMode = "none" | "surplus" | "all";

export const ROLLOVER_MODES: readonly RolloverMode[] = ["none", "surplus", "all"];

/** Local-only sync bookkeeping, present on every mirrored row. */
export interface SyncMeta {
  /** Target-first move intent, local only; survives before source meta is written. */
  _moveFrom?: { kind: "personal" | "family"; ownerId: string };
  _createdBy?: string;
  _updatedBy?: string;
  /** 1 = has local changes not yet pushed. Indexed, so the push query is cheap. */
  _dirty: 0 | 1;
  /** ISO timestamp of the last successful push, or null if never pushed. */
  _syncedAt: string | null;
  /**
   * The fields of the row's last pulled payload that this client does not
   * know — kept so they go back up untouched (DATA_MODEL §4, "Unknown fields").
   *
   * Without it, a newer client's field was erased by any older install that
   * edited the row: `fromWire*` drops what it does not recognise, the stripped
   * row was pushed, and last-write-wins made the loss permanent everywhere.
   * Absent when there were none. Never sent as itself: `sync.ts` spreads it
   * *under* the known fields, so a stale copy of a known key can never win.
   */
  _extra?: Record<string, unknown>;
}

export interface Profile {
  id: string;
  currency: string;
  timezone: string;
  cycleStartDay: number;
  rolloverMode: RolloverMode;
  onboardedAt: string | null;
  /**
   * The category Capture opens on, or null for "the last one I used". Optional
   * so a row from before it existed validates (`fromWireProfile` fills null),
   * and only meaningful while that category is live: deleting or archiving it
   * clears this in the same write (`repo.ts`).
   */
  defaultCategoryId?: string | null;
  /** Personal Capture ordering across both ledgers. */
  gridOrder?: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  userId: string;
  name: string;
  color: string;
  sortOrder: number;
  archivedAt: string | null;
  rolloverOverride: RolloverMode | null;
  isFallback: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  /**
   * Optional budget-sheet fields (DATA_MODEL §2, §8), so an old row (pre-v4,
   * pre-import) validates without them and `fromWireCategory` fills an absent
   * one with `null`.
   *
   * `note` and `keepInAccount` still carry **no behaviour**. `savingsOrExpenditure`
   * no longer: see below.
   */
  note?: string | null;
  /**
   * Savings or expenditure — and this **drives math** (scope expansion
   * 2026-07-31). A savings category leaves the expenditure world: it never
   * appears on the numpad, its budget is subtracted from the salary to give the
   * expense ceiling, and nothing is ever logged against it.
   *
   * **Expenditure must be stated.** Blank or absent means savings. Read it only
   * through `isSavingsCategory` in `src/domain/category_kind.ts` — never compare
   * this string directly, and never assume `null` means expenditure the way the
   * pre-v6 round-trip convention did.
   */
  savingsOrExpenditure?: string | null;
  /** A free-text account label the user keeps this category's money in (e.g. "IBKR"). */
  keepInAccount?: string | null;
  /**
   * One emoji, drawn on the category's colour disc. Optional — and never read
   * directly: `categoryIcon()` (`src/domain/category_icon.ts`) falls back to
   * the seeded icon, then a neutral tag, because an older cached client can
   * strip it. The read boundary checks only "string or null"; `repo.ts` checks
   * "exactly one emoji" on the way in.
   */
  icon?: string | null;
}

export interface Budget {
  id: string;
  userId: string;
  categoryId: string;
  amountCents: Cents | null;
  /** `YYYY-MM-DD`. The budget in force is the latest row at or before a period's start. */
  effectiveFrom: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * A monthly salary, versioned exactly as a budget is.
 *
 * There is no `categoryId`: this is one figure for the account. It is the
 * numerator of the expense ceiling (`salary − Σ savings budgets`) and nothing
 * else reads it. Deliberately not an income ledger — one amount, versioned, with
 * no source, payee or type (scope expansion 2026-07-31).
 */
export interface Salary {
  id: string;
  userId: string;
  amountCents: Cents;
  /** `YYYY-MM-DD`. The salary in force is the latest row at or before a period's start. */
  effectiveFrom: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface RecurringRule {
  id: string;
  userId: string;
  categoryId: string;
  amountCents: Cents;
  dayOfMonth: number;
  note: string | null;
  active: boolean;
  /** `YYYY-MM-DD`, or null. Stops two devices materialising the same rule twice. */
  lastMaterialisedOn: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface Expense {
  splitRef?: { groupId: string; splitExpenseId: string; linkId?: string; homeRateNano?: RateNano | null; homeCurrency?: string; splitUpdatedAt?: string; groupKey?: string } | null;
  id: string;
  userId: string;
  categoryId: string;
  amountCents: Cents;
  /** ISO instant. The ONLY temporal fact stored — period is derived (INV-2). */
  occurredAt: string;
  note: string | null;
  recurringRuleId: string | null;
  /**
   * The `soundingActions` row that created this expense, or null for one typed
   * on the numpad.
   *
   * A **convenience**, not the record. The authority on "which expenses did the
   * agent create" is the action row's own `expenseIds`, because an install that
   * predates this field drops it on the way through `fromWireExpense` and would
   * erase it on the next edit — whereas an unknown *table* is skipped whole by
   * an old client (`sync.ts`), so the action row cannot be damaged the same way.
   */
  sourceId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * One conversation with the agent, kept so that leaving the screen is not the
 * same as losing it.
 *
 * **Cleared after three days**, from last activity rather than creation, so an
 * exchange still being worked through is never cut off mid-use. Clearing blanks
 * `title` and `entries` *before* tombstoning: a tombstone still carries its
 * payload to every other device and is never purged, so a row that was only
 * marked deleted would keep every word of the conversation in D1 permanently —
 * a clearance the user was promised and did not get.
 */
export interface Sounding {
  id: string;
  userId: string;
  /** Derived from the first thing said or the document's name. Blank once cleared. */
  title: string;
  /** The transcript, as JSON. Blank once cleared. */
  entriesJson: string;
  /**
   * Turns dropped to keep the row under the wire's payload ceiling.
   *
   * Counted rather than silently discarded: a transcript that quietly loses its
   * beginning is a record you would trust and should not (INV-7).
   */
  droppedEntries: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * One save the agent made — the permanent half.
 *
 * Outlives the conversation that produced it, which is the entire point of it
 * being a second table. The expenses stay in the ledger forever; a record of
 * where they came from that expired in three days would be an audit trail that
 * is never available when it is wanted.
 */
export interface SoundingAction {
  id: string;
  userId: string;
  /** The conversation. May already be cleared — the action outlives it. */
  soundingId: string;
  at: string;
  /** What was written, in the app's own words: "12 expenses added". */
  summary: string;
  totalCents: Cents;
  /** The rows created. This is the authoritative provenance link (see `Expense.sourceId`). */
  expenseIds: string[];
  /** Owner split rows created by this save; absent on older actions. */
  splitExpenseIds?: string[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Owner-written personal-stream rows; balances are derived, never persisted. */
interface SplitRow {
  id: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}
export interface SplitMember { id: string; name: string; archivedAt: string | null }
export interface SplitGroup extends SplitRow {
  name: string;
  baseCurrency: string;
  members: SplitMember[];
  meMemberId: string;
  rateHints: Record<string, RateNano>;
  homeRateNano: RateNano | null;
  archivedAt: string | null;
}
export interface SplitItem { label: string; amountMinor: Minor; memberIds: string[] }
export interface SplitExpense extends SplitRow {
  groupId: string;
  description: string;
  occurredAt: string;
  amountMinor: Minor;
  currency: string;
  rateNano: RateNano;
  homeRateNano: RateNano | null;
  payerMemberId: string;
  mode: SplitMode;
  /** Order breaks residual ties and must survive storage and sync unchanged. */
  entries: SplitEntry[];
  items: SplitItem[] | null;
}
export interface SplitTransfer extends SplitRow {
  groupId: string;
  from: string;
  to: string;
  amountMinor: Minor;
  occurredAt: string;
  note: string | null;
}
/** Read-only linked cache reserved for the separate linked pull stream. */
export interface LinkedSplitRow { linkId: string; table: string; rowId: string; payload: string; updatedAt?: string; deleted?: boolean }
export type LocalSplitGroup = SplitGroup & SyncMeta;
export type LocalSplitExpense = SplitExpense & SyncMeta;
export type LocalSplitTransfer = SplitTransfer & SyncMeta;

export type LocalProfile = Profile & SyncMeta;
export type LocalCategory = Category & SyncMeta;
export type LocalBudget = Budget & SyncMeta;
export type LocalSalary = Salary & SyncMeta;
export type LocalRecurringRule = RecurringRule & SyncMeta;
export type LocalExpense = Expense & SyncMeta;
export type LocalSounding = Sounding & SyncMeta;
export type LocalSoundingAction = SoundingAction & SyncMeta;

/* ── validation primitives ─────────────────────────────────────────────────── */

export class WireRowError extends Error {}

function asRecord(value: unknown, table: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new WireRowError(`${table}: expected an object, received ${typeof value}`);
  }
  return value as Record<string, unknown>;
}

function str(row: Record<string, unknown>, key: string, table: string): string {
  const v = row[key];
  if (typeof v !== "string") {
    throw new WireRowError(`${table}.${key}: expected string, received ${describe(v)}`);
  }
  return v;
}

function nullableStr(row: Record<string, unknown>, key: string, table: string): string | null {
  const v = row[key];
  if (v === null || v === undefined) return null;
  if (typeof v !== "string") {
    throw new WireRowError(`${table}.${key}: expected string or null, received ${describe(v)}`);
  }
  return v;
}

/** `""` and `"   "` are not a value. Used where blank must read as absent. */
function blankToNull(value: string | null): string | null {
  return value === null || value.trim() === "" ? null : value;
}

function int(row: Record<string, unknown>, key: string, table: string): number {
  const v = row[key];
  if (typeof v !== "number" || !Number.isInteger(v)) {
    throw new WireRowError(`${table}.${key}: expected an integer, received ${describe(v)}`);
  }
  return v;
}

function bool(row: Record<string, unknown>, key: string, table: string): boolean {
  const v = row[key];
  if (typeof v !== "boolean") {
    throw new WireRowError(`${table}.${key}: expected boolean, received ${describe(v)}`);
  }
  return v;
}

function money(row: Record<string, unknown>, key: string, table: string): Cents {
  const v = row[key];
  // Money crosses the wire as a JSON number of cents. Our ceiling is well inside
  // the safe-integer range, so a non-integer here means the row drifted — and
  // `cents()` throws on one rather than rounding it (INV-1).
  if (typeof v !== "number") {
    throw new WireRowError(`${table}.${key}: expected a number of cents, received ${describe(v)}`);
  }
  return cents(v);
}

function rollover(
  row: Record<string, unknown>,
  key: string,
  table: string,
  nullable: boolean,
): RolloverMode | null {
  const v = row[key];
  if ((v === null || v === undefined) && nullable) return null;
  if (typeof v !== "string" || !ROLLOVER_MODES.includes(v as RolloverMode)) {
    throw new WireRowError(`${table}.${key}: expected one of ${ROLLOVER_MODES.join("/")}, received ${describe(v)}`);
  }
  return v as RolloverMode;
}

function describe(v: unknown): string {
  if (v === null) return "null";
  if (v === undefined) return "undefined";
  switch (typeof v) {
    case "string":
      return `string ${JSON.stringify(v)}`;
    case "number":
    case "boolean":
      return `${typeof v} ${String(v)}`;
    case "bigint":
      return `bigint ${v.toString()}`;
    case "symbol":
      return v.toString();
    case "function":
      return "function";
    default:
      return `object ${JSON.stringify(v)}`;
  }
}

/* ── wire → app ────────────────────────────────────────────────────────── */

export function fromWireProfile(input: unknown): Profile {
  const r = asRecord(input, "profiles");
  return {
    id: str(r, "id", "profiles"),
    currency: str(r, "currency", "profiles"),
    timezone: str(r, "timezone", "profiles"),
    cycleStartDay: int(r, "cycleStartDay", "profiles"),
    rolloverMode: rollover(r, "rolloverMode", "profiles", false) as RolloverMode,
    onboardedAt: nullableStr(r, "onboardedAt", "profiles"),
    defaultCategoryId: nullableStr(r, "defaultCategoryId", "profiles"),
    gridOrder: readGridOrder(r.gridOrder),
    createdAt: str(r, "createdAt", "profiles"),
    updatedAt: str(r, "updatedAt", "profiles"),
  };
}

export function fromWireCategory(input: unknown): Category {
  const r = asRecord(input, "categories");
  return {
    id: str(r, "id", "categories"),
    userId: str(r, "userId", "categories"),
    name: str(r, "name", "categories"),
    color: str(r, "color", "categories"),
    sortOrder: int(r, "sortOrder", "categories"),
    archivedAt: nullableStr(r, "archivedAt", "categories"),
    rolloverOverride: rollover(r, "rolloverOverride", "categories", true),
    isFallback: bool(r, "isFallback", "categories"),
    createdAt: str(r, "createdAt", "categories"),
    updatedAt: str(r, "updatedAt", "categories"),
    deletedAt: nullableStr(r, "deletedAt", "categories"),
    // Optional budget-sheet fields. `nullableStr` tolerates their absence on a
    // row written by an older client (→ null), which is what makes the schema
    // change forward- and backward-compatible without a protocol bump.
    note: nullableStr(r, "note", "categories"),
    // **Absent or null means "written before v6", and is repaired here.**
    //
    // The Dexie `version(6)` upgrade stamps local rows, but it cannot reach D1 —
    // and a device starting from an empty database runs no upgrade at all. Sign
    // out and back in, reinstall, add a second device, or let iOS evict an
    // uninstalled PWA's storage, and the first pull would otherwise deliver
    // every pre-v6 category as savings: the numpad empties to the fallback and
    // the whole ledger leaves the spending history.
    //
    // This is safe precisely because no v6 client ever *writes* null — the
    // seeder, `addCategory`, the Settings control and the sheet parser all write
    // an explicit label, including for a blank cell, which means savings. So
    // null on the wire carries exactly one meaning and it is this one.
    // An empty or whitespace-only string counts as absent for the same reason.
    // "Blank means savings" is a rule about a **cell in a spreadsheet**, where
    // the user chose to leave it empty; on the wire there is no such intent to
    // read, and `""` is indistinguishable from "no label was ever recorded". The
    // conservative direction is the only safe one here — guessing savings
    // retires a category and its whole history with nothing on screen to say so.
    savingsOrExpenditure:
      blankToNull(nullableStr(r, "savingsOrExpenditure", "categories")) ?? EXPENDITURE_LABEL,
    keepInAccount: nullableStr(r, "keepInAccount", "categories"),
    icon: nullableStr(r, "icon", "categories"),
  };
}

export function fromWireBudget(input: unknown): Budget {
  const r = asRecord(input, "budgets");
  return {
    id: str(r, "id", "budgets"),
    userId: str(r, "userId", "budgets"),
    categoryId: str(r, "categoryId", "budgets"),
    amountCents: r.amountCents === null ? null : money(r, "amountCents", "budgets"),
    effectiveFrom: str(r, "effectiveFrom", "budgets"),
    createdAt: str(r, "createdAt", "budgets"),
    updatedAt: str(r, "updatedAt", "budgets"),
    deletedAt: nullableStr(r, "deletedAt", "budgets"),
  };
}

export function fromWireSalary(input: unknown): Salary {
  const r = asRecord(input, "salaries");
  return {
    id: str(r, "id", "salaries"),
    userId: str(r, "userId", "salaries"),
    amountCents: money(r, "amountCents", "salaries"),
    effectiveFrom: str(r, "effectiveFrom", "salaries"),
    createdAt: str(r, "createdAt", "salaries"),
    updatedAt: str(r, "updatedAt", "salaries"),
    deletedAt: nullableStr(r, "deletedAt", "salaries"),
  };
}

export function fromWireRecurringRule(input: unknown): RecurringRule {
  const r = asRecord(input, "recurring_rules");
  return {
    id: str(r, "id", "recurring_rules"),
    userId: str(r, "userId", "recurring_rules"),
    categoryId: str(r, "categoryId", "recurring_rules"),
    amountCents: money(r, "amountCents", "recurring_rules"),
    dayOfMonth: int(r, "dayOfMonth", "recurring_rules"),
    note: nullableStr(r, "note", "recurring_rules"),
    active: bool(r, "active", "recurring_rules"),
    lastMaterialisedOn: nullableStr(r, "lastMaterialisedOn", "recurring_rules"),
    createdAt: str(r, "createdAt", "recurring_rules"),
    updatedAt: str(r, "updatedAt", "recurring_rules"),
    deletedAt: nullableStr(r, "deletedAt", "recurring_rules"),
  };
}

export function fromWireExpense(input: unknown): Expense {
  const r = asRecord(input, "expenses");
  return {
    id: str(r, "id", "expenses"),
    userId: str(r, "userId", "expenses"),
    categoryId: str(r, "categoryId", "expenses"),
    amountCents: money(r, "amountCents", "expenses"),
    occurredAt: str(r, "occurredAt", "expenses"),
    note: nullableStr(r, "note", "expenses"),
    recurringRuleId: nullableStr(r, "recurringRuleId", "expenses"),
    // Optional on the wire, unlike every field above it. Rows written before
    // provenance existed simply do not carry it, and throwing on those would
    // quarantine an entire ledger's history to add a field nothing depends on.
    sourceId: r.sourceId === undefined ? null : nullableStr(r, "sourceId", "expenses"),
    ...("splitRef" in r ? { splitRef: readSplitRef(r.splitRef) } : {}),
    createdAt: str(r, "createdAt", "expenses"),
    updatedAt: str(r, "updatedAt", "expenses"),
    deletedAt: nullableStr(r, "deletedAt", "expenses"),
  };
}

export function fromWireSounding(input: unknown): Sounding {
  const r = asRecord(input, "soundings");
  return {
    id: str(r, "id", "soundings"),
    userId: str(r, "userId", "soundings"),
    // A cleared row carries "" for both, which is a valid string — clearing is
    // not the same as malformed, and must not be quarantined as if it were.
    title: str(r, "title", "soundings"),
    entriesJson: str(r, "entriesJson", "soundings"),
    droppedEntries: int(r, "droppedEntries", "soundings"),
    createdAt: str(r, "createdAt", "soundings"),
    updatedAt: str(r, "updatedAt", "soundings"),
    deletedAt: nullableStr(r, "deletedAt", "soundings"),
  };
}

export function fromWireSoundingAction(input: unknown): SoundingAction {
  const r = asRecord(input, "sounding_actions");
  const ids = r.expenseIds;
  if (!Array.isArray(ids) || ids.some((id) => typeof id !== "string")) {
    throw new WireRowError(`sounding_actions: expenseIds must be an array of strings, received ${describe(ids)}`);
  }
  const splitIds = r.splitExpenseIds;
  if (splitIds !== undefined && (!Array.isArray(splitIds) || splitIds.some(id => typeof id !== "string"))) throw new WireRowError("sounding_actions: splitExpenseIds must be an array of strings");
  return {
    id: str(r, "id", "sounding_actions"),
    userId: str(r, "userId", "sounding_actions"),
    soundingId: str(r, "soundingId", "sounding_actions"),
    at: str(r, "at", "sounding_actions"),
    summary: str(r, "summary", "sounding_actions"),
    totalCents: money(r, "totalCents", "sounding_actions"),
    expenseIds: ids as string[],
    ...(splitIds !== undefined ? { splitExpenseIds: splitIds as string[] } : {}),
    createdAt: str(r, "createdAt", "sounding_actions"),
    updatedAt: str(r, "updatedAt", "sounding_actions"),
    deletedAt: nullableStr(r, "deletedAt", "sounding_actions"),
  };
}

/**
 * The keys of a raw payload that its validated row does not carry — what this
 * client does not know, to be kept in `_extra` and sent back untouched.
 *
 * `undefined` when there are none, so a row with nothing unknown stores no
 * empty object. Local bookkeeping keys are never "unknown": a payload that
 * somehow carried `_dirty` must not smuggle it back into Dexie as data.
 */
export function unknownFields(
  raw: unknown,
  known: object,
): Record<string, unknown> | undefined {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return undefined;
  const extra: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (key in known || key.startsWith("_") || value === undefined) continue;
    extra[key] = value;
  }
  return Object.keys(extra).length > 0 ? extra : undefined;
}

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value) || value.some(id => typeof id !== "string")) throw new WireRowError("profile.gridOrder must be an array of category ids");
  return value as string[];
}

let droppedGridOrders = 0;
export function getDroppedGridOrderCount(): number { return droppedGridOrders; }
function readGridOrder(value: unknown): string[] | null {
  if (value == null) return null;
  try { return stringArray(value); }
  catch { droppedGridOrders += 1; console.warn("Invalid capture order dropped; the profile was retained."); return null; }
}


function nonblank(row: Record<string, unknown>, key: string, table: string): string {
  const value = str(row, key, table);
  if (value.trim() === "") throw new WireRowError(`${table}.${key}: expected a nonblank string.`);
  return value;
}
function array(value: unknown, table: string): unknown[] {
  if (!Array.isArray(value)) throw new WireRowError(`${table}: expected an array.`);
  return value;
}
function currency(row: Record<string, unknown>, key: string, table: string): string {
  const value = str(row, key, table);
  if (!isCurrencyCode(value)) throw new WireRowError(`${table}.${key}: unsupported currency ${value}.`);
  return value;
}
function nano(row: Record<string, unknown>, key: string, table: string): RateNano {
  const value = int(row, key, table);
  if (!Number.isSafeInteger(value) || value < 1 || value > MAX_RATE_NANO) throw new WireRowError(`${table}.${key}: invalid rate.`);
  return rateNano(value);
}
function inputMinor(row: Record<string, unknown>, key: string, table: string): Minor {
  const input = int(row, key, table);
  if (!Number.isSafeInteger(input)) throw new WireRowError(`${table}.${key}: unsafe minor units.`);
  const value = minor(input);
  if (value < 0 || value > MAX_MINOR_INPUT) throw new WireRowError(`${table}.${key}: amount outside input bounds.`);
  return value;
}
function instant(row: Record<string, unknown>, key: string, table: string): string {
  const value = str(row, key, table);
  if (!Number.isFinite(Date.parse(value))) throw new WireRowError(`${table}.${key}: invalid timestamp.`);
  return value;
}
function splitInstant(row: Record<string, unknown>, key: string, table: string): string {
  const value = str(row, key, table);
  assertSplitOccurredAt(value);
  return value;
}
function splitRow(row: Record<string, unknown>, table: string): SplitRow {
  return { id: nonblank(row, "id", table), userId: nonblank(row, "userId", table),
    createdAt: instant(row, "createdAt", table), updatedAt: instant(row, "updatedAt", table), deletedAt: nullableStr(row, "deletedAt", table) };
}
function readSplitRef(value: unknown): Expense["splitRef"] {
  if (value === undefined || value === null) return null;
  const r = asRecord(value, "expenses.splitRef");
  return { ...("linkId" in r || "homeRateNano" in r ? { homeRateNano: r.homeRateNano === null ? null : nano(r, "homeRateNano", "expenses.splitRef") } : {}), ...("linkId" in r ? { linkId: nonblank(r, "linkId", "expenses.splitRef") } : {}), groupId: nonblank(r, "groupId", "expenses.splitRef"), splitExpenseId: nonblank(r, "splitExpenseId", "expenses.splitRef"),
    ...("homeCurrency" in r ? { homeCurrency: currency(r, "homeCurrency", "expenses.splitRef") } : {}),
    ...("splitUpdatedAt" in r ? { splitUpdatedAt: str(r, "splitUpdatedAt", "expenses.splitRef") } : {}),
    ...("groupKey" in r ? { groupKey: str(r, "groupKey", "expenses.splitRef") } : {}) };
}
export function fromWireSplitGroup(input: unknown): SplitGroup {
  const table = "split_groups";
  const r = asRecord(input, table);
  const members = array(r.members, table).map(value => {
    const m = asRecord(value, `${table}.members`);
    return { id: nonblank(m, "id", table), name: nonblank(m, "name", table), archivedAt: nullableStr(m, "archivedAt", table) };
  });
  if (members.length > MAX_SPLIT_MEMBERS) throw new WireRowError(`${table}: use at most ${MAX_SPLIT_MEMBERS} members; remove an unused member before adding more.`);
  const ids = members.map(m => m.id);
  if (new Set(ids).size !== ids.length) throw new WireRowError(`${table}: duplicate member ids.`);
  const meMemberId = nonblank(r, "meMemberId", table);
  if (!ids.includes(meMemberId)) throw new WireRowError(`${table}: unknown owner member.`);
  const hints = asRecord(r.rateHints, `${table}.rateHints`);
  const rateHints: Record<string, RateNano> = {};
  for (const key of Object.keys(hints)) {
    if (!isCurrencyCode(key)) throw new WireRowError(`${table}: unsupported hint currency ${key}.`);
    rateHints[key] = nano(hints, key, table);
  }
  return { ...splitRow(r, table), name: nonblank(r, "name", table), baseCurrency: currency(r, "baseCurrency", table), members, meMemberId,
    rateHints, homeRateNano: r.homeRateNano == null ? null : nano(r, "homeRateNano", table), archivedAt: nullableStr(r, "archivedAt", table) };
}
export function fromWireSplitExpense(input: unknown): SplitExpense {
  const table = "split_expenses";
  const r = asRecord(input, table);
  if (!("homeRateNano" in r)) throw new SplitRowError("Missing homeRateNano. Open the split to set its home rate and save again.");
  const entries = array(r.entries, table).map(value => {
    const e = asRecord(value, `${table}.entries`);
    return { memberId: nonblank(e, "memberId", table), value: int(e, "value", table) };
  });
  const mode = str(r, "mode", table);
  const amountMinor = inputMinor(r, "amountMinor", table);
  const ccy = currency(r, "currency", table);
  const result = validateSplit({ mode, amountMinor, currency: ccy, entries, memberIds: entries.map(e => e.memberId) });
  if (!result.ok) throw new WireRowError(`${result.reason}: ${result.detail}`);
  const items = r.items == null ? null : array(r.items, table).map(value => {
    const item = asRecord(value, `${table}.items`);
    const memberIds = array(item.memberIds, table).map(id => {
      if (typeof id !== "string" || id.trim() === "") throw new WireRowError(`${table}: invalid item member id.`);
      return id;
    });
    if (new Set(memberIds).size !== memberIds.length) throw new WireRowError(`${table}: duplicate item member ids.`);
    return { label: str(item, "label", table), amountMinor: inputMinor(item, "amountMinor", table), memberIds };
  });
  return { ...splitRow(r, table), groupId: nonblank(r, "groupId", table), description: str(r, "description", table),
    occurredAt: splitInstant(r, "occurredAt", table), amountMinor, currency: ccy, rateNano: nano(r, "rateNano", table),
    homeRateNano: r.homeRateNano == null ? null : nano(r, "homeRateNano", table),
    payerMemberId: nonblank(r, "payerMemberId", table), mode: mode as SplitMode, entries, items };
}
export function fromWireSplitTransfer(input: unknown): SplitTransfer {
  const table = "split_transfers";
  const r = asRecord(input, table);
  const from = nonblank(r, "from", table), to = nonblank(r, "to", table);
  const amountMinor = inputMinor(r, "amountMinor", table);
  const result = validateTransfer({ from, to, amountMinor, memberIds: [from, to] });
  if (!result.ok) throw new WireRowError(`${result.reason}: ${result.detail}`);
  return { ...splitRow(r, table), groupId: nonblank(r, "groupId", table), from, to, amountMinor,
    occurredAt: splitInstant(r, "occurredAt", table), note: nullableStr(r, "note", table) };
}

/** Linked projections deliberately omit account identity. The marker never leaves validation. */
export type LinkedGroup = Omit<SplitGroup, "userId">;
export type LinkedExpense = Omit<SplitExpense, "userId">;
export type LinkedTransfer = Omit<SplitTransfer, "userId">;
function ownerless<T extends { userId: string }>(input: unknown, validator: (input: unknown) => T): Omit<T, "userId"> {
  const row = asRecord(input, "linked split");
  const { userId: _owner, ...result } = validator({ ...row, userId: "linked-projection" });
  void _owner;
  return result;
}
export const fromWireLinkedGroup = (input: unknown): LinkedGroup => ownerless(input, fromWireSplitGroup);
export const fromWireLinkedExpense = (input: unknown): LinkedExpense => ownerless(input, fromWireSplitExpense);
export const fromWireLinkedTransfer = (input: unknown): LinkedTransfer => ownerless(input, fromWireSplitTransfer);
